/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { GridApi } from 'ag-grid-community'
import { useEffect, useRef, useState } from 'react'

import colors from '@sportninja/common/constants/appColors'

import type { BulkAction } from '@sportninja/common/features/game/gameListView/bulkPlan'
import { planBulkEdit } from '@sportninja/common/features/game/gameListView/bulkPlan'
import type {
  CivilDateRange,
  GameLike,
  VenueRef,
} from '@sportninja/common/features/game/gameListView/types'
import { spansMultipleZones } from '@sportninja/common/features/game/gameListView/venueLocalDateTime'

import { zIndex } from 'src/components/css'
import { useModalScroll } from 'src/hooks/useModalScroll'

import type { OfficialTypeOption } from './OfficialsCellEditor'
import type { GameTypeOption } from '../hooks/useDetailsColumns'
import { useOfficialOptions } from '../hooks/useOfficialOptions'
import { useTeamOptions } from '../hooks/useTeamOptions'
import type { GameListCounts } from '../hooks/useGameListItems'
import {
  MIN_DISPLAYABLE_ROWS,
  RAW_PAGE_AUTO_FOLLOW_CAP,
} from '../hooks/useGameListItems'
import { GameModalContainer } from '../../gameForm'
import { useGameBulkEdit } from '../hooks/useGameBulkEdit'
import type { RowResult } from '../hooks/useGameBulkEdit'
import { useGridToolbar } from '../hooks/useGridToolbar'
import { buildGameGridRows } from '../utils/gameGridRows'
import type { GameGridRow } from '../utils/gameGridRows'
import { resolveEditModalScheduleId } from '../utils/editModalScheduleId'
import { shouldExitFullscreen } from '../utils/fullscreenShortcut'
import { gameStatusFullLabel } from '../utils/gameStatusLabel'
import { gameViewsLabel } from '../utils/i18n'
import BulkFailurePanel from './BulkFailurePanel'
import BulkToolbar from './BulkToolbar'
import DetailsGrid from './DetailsGrid'
import GridStatusBar from './GridStatusBar'
import GridToolbar from './GridToolbar'
import LoadMoreFooter from './LoadMoreFooter'

export interface DetailsPanelProps {
  /** Games inside the active civil window — what the view normally shows. */
  games: GameLike[]
  /**
   * Every loaded game, unfiltered. Needed because a row the admin just moved
   * out of the window is no longer in `games`, and it has to stay on screen.
   */
  rawGames: GameLike[]
  venuesById: Map<string, VenueRef>
  gameTypes: GameTypeOption[]
  /**
   * Every official role. Sourced from Redux and deliberately NOT filtered by
   * sport — see `useDetailsColumns`.
   */
  officialTypes: OfficialTypeOption[]
  /**
   * The organization and competition in scope. One pair, two consumers.
   *
   * The officials pool needs BOTH before it can be fetched: unscoped, that
   * endpoint returns every official on the platform. Absent on the Team and My
   * Schedule hosts, which is what makes the officials columns read-only there.
   *
   * The edit modal uses the same pair to seed its option lists, and
   * `organizationId` additionally enables its "create a new location" row.
   */
  organizationId?: string | null
  scheduleId?: string | null
  civilRange: CivilDateRange | null
  canEdit: boolean
  loading: boolean
  counts: GameListCounts
  isFetchingNextPage: boolean
  /** Called when the grid is scrolled near its bottom — same idea as List. */
  onReachBottom: () => void
  isError?: boolean
  errorMessage?: string
  onRetry?: () => void
  isLoadingAll?: boolean
  onLoadAll?: () => void
  queryKey: unknown[]
  /** Labels the edit modal's "Within <org>" location-scope toggle. */
  organizationName?: string | null
  sportId?: string | null
  /** Refetches the grid after the edit modal saves or deletes. */
  onGameChanged?: () => void
  /**
   * Whether the edit modal offers Delete. The grid's rows are the list
   * resource, which carries neither `permission.delete` nor the tournament
   * flag, so the answer has to come from the competition page.
   */
  canDeleteGames?: boolean
}

/**
 * The spreadsheet view: one screen, every game, editable in place.
 *
 * Single-cell and bulk edits share one path — `planBulkEdit` produces the
 * patches either way — so validation, DST handling, skip reasons and
 * reconciliation are identical whether the admin retimes one game or ten.
 */
const DetailsPanel = ({
  games,
  rawGames,
  venuesById,
  gameTypes,
  officialTypes,
  organizationId,
  scheduleId,
  civilRange,
  canEdit,
  loading,
  counts,
  isFetchingNextPage,
  onReachBottom,
  isError,
  errorMessage,
  onRetry,
  isLoadingAll,
  onLoadAll,
  queryKey,
  organizationName,
  sportId,
  onGameChanged,
  canDeleteGames,
}: DetailsPanelProps) => {
  'use memo'

  const [editingRow, setEditingRow] = useState<GameGridRow | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const [gridStatus, setGridStatus] = useState({
    displayed: 0,
    isFiltered: false,
  })

  /*
   * Full screen is LOCAL state, not a URL param.
   *
   * The other view controls live in the address bar because they change what
   * you are looking at and a link should carry them. This changes how much of
   * one screen a sheet is allowed to take, which is a preference of the moment
   * — pinning it in a shared link would open someone else's browser in a mode
   * they did not ask for and cannot see their way out of.
   */
  const [isFullscreen, setIsFullscreen] = useState(false)
  const { openModal, closeModal } = useModalScroll()

  /*
   * The shell node itself, because AG Grid needs it as a `popupParent`.
   *
   * State rather than a ref: `popupParent` is a grid option, and the grid has
   * to re-render with the real node once it exists — a ref's `.current` is null
   * on the render that mounts it, and nothing would ever tell the grid it had
   * filled in. The callback fires once on mount, so this does not churn.
   */
  const [shellEl, setShellEl] = useState<HTMLDivElement | null>(null)

  //  The page behind is still a scrollable document. Left alone, a wheel event
  //  that the grid does not consume scrolls it, and exiting drops the admin
  //  somewhere they never navigated to.
  useEffect(() => {
    if (!isFullscreen) {
      return
    }
    openModal()
    return () => closeModal()
  }, [isFullscreen, openModal, closeModal])

  /*
   * Escape leaves. The shell covers the whole window including the nav, so the
   * button is the only way out — and a cell editor swallows the first Escape to
   * cancel its own edit, which is the behaviour a spreadsheet should have.
   * Bound in the CAPTURE phase on the window so it fires wherever focus sits,
   * including inside a body-portalled picker panel.
   */
  useEffect(() => {
    if (!isFullscreen) {
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      //  An open editor owns the first Escape; it closes, and the next one
      //  leaves full screen.
      const editingCells = gridApi?.getEditingCells() ?? []
      if (shouldExitFullscreen(event.key, editingCells.length)) {
        setIsFullscreen(false)
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [isFullscreen, gridApi])

  const toolbar = useGridToolbar({ api: gridApi })

  const { teamsById, isLoading: teamsLoading } = useTeamOptions(
    scheduleId,
    rawGames
  )

  const bulk = useGameBulkEdit({ queryKey, venuesById, teamsById, civilRange })

  //  Unioned with the officials already on loaded games, so someone outside the
  //  search pool still renders with a name rather than a bare uid.
  const officialOptions = useOfficialOptions(
    organizationId,
    scheduleId,
    rawGames
  )

  const failuresById = new Map<string, RowResult>(
    (bulk.summary?.results ?? [])
      .filter((result) => result.outcome !== 'saved')
      .map((result) => [result.gameId, result])
  )

  /*
   * Rows that moved out of the active filter stay in the grid until the summary
   * is dismissed — otherwise the row the admin just edited vanishes the instant
   * it is patched, which reads as "my edit deleted the game".
   *
   * The row has to be pulled back out of the UNFILTERED list: by the time the
   * optimistic patch lands, the civil-range filter has already dropped it from
   * `games`, so decorating `games` alone can only ever mark rows that happened
   * to survive filtering.
   */
  const visibleIds = new Set(games.map((game) => game.id))
  const retainedGames =
    bulk.retainedOutsideFilterIds.size === 0
      ? games
      : [
          ...games,
          ...rawGames.filter(
            (game) =>
              bulk.retainedOutsideFilterIds.has(game.id) &&
              !visibleIds.has(game.id)
          ),
        ]
  const rows = buildGameGridRows(retainedGames, {
    pendingIds: bulk.pendingIds,
    retainedOutsideFilterIds: bulk.retainedOutsideFilterIds,
    failuresById,
    tbdLabel: gameViewsLabel('gameGridTbd'),
    statusLabel: gameStatusFullLabel,
  })

  const rowsById = new Map(rows.map((row) => [row.id, row]))
  const selectedGames = selectedIds
    .map((id) => rowsById.get(id)?.game)
    .filter((game): game is GameLike => game !== undefined)

  const showTimeZoneInHeader = spansMultipleZones(
    rows.map((row) => row.timeZone)
  )

  /**
   * Grid filters are CLIENT-side over the pages loaded so far, and paging here
   * is driven by scrolling. Filter a season down to two rows and there is
   * nothing left to scroll — so the next page never loads, and a search that
   * would have matched forty games reports two.
   *
   * This is the same unsticking `useGameListItems` does for the civil-date
   * filter, against the same page budget: pull pages while a filter is active
   * and the result is too short to scroll, then stop and let the status strip
   * say which population the count came from.
   */
  const filterFollowedRef = useRef(0)

  useEffect(() => {
    if (!gridStatus.isFiltered) {
      filterFollowedRef.current = 0
      return
    }
    if (
      !counts.hasUnloadedRawPages ||
      isFetchingNextPage ||
      gridStatus.displayed >= MIN_DISPLAYABLE_ROWS ||
      filterFollowedRef.current >= RAW_PAGE_AUTO_FOLLOW_CAP
    ) {
      return
    }
    filterFollowedRef.current += 1
    onReachBottom()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gridStatus.isFiltered,
    gridStatus.displayed,
    counts.hasUnloadedRawPages,
    isFetchingNextPage,
  ])

  const preview = (action: BulkAction) => {
    const plan = planBulkEdit(selectedGames, action, { venuesById, teamsById })
    return { affected: plan.patches.length, skipped: plan.skipped.length }
  }

  const applyBulk = (action: BulkAction) => {
    const plan = planBulkEdit(selectedGames, action, { venuesById, teamsById })
    bulk.runPatches(selectedGames, plan.patches, plan.skipped)
  }

  const applyCell = (row: GameGridRow, action: BulkAction) => {
    const plan = planBulkEdit([row.game], action, { venuesById, teamsById })
    bulk.runPatches([row.game], plan.patches, plan.skipped)
  }

  const describeRow = (gameId: string): string => {
    const row = rowsById.get(gameId)
    if (row === undefined) {
      return gameId
    }
    return `${row.homeLabel} ${gameViewsLabel('gameGridVersus')} ${
      row.awayLabel
    }`
  }

  return (
    /*
     * TWO elements, always — the shell and the column inside it.
     *
     * The shell is what AG Grid parents its popups to in full screen, and a
     * popup is positioned against its parent's PADDING box. Any padding here
     * would offset every dropdown by exactly that much, so the inset lives on
     * the inner column instead. Both nodes exist in either mode so that
     * toggling changes classes and never the tree — remounting would take the
     * grid's scroll, selection and focus with it.
     */
    <div
      ref={setShellEl}
      css={[shellStyles, isFullscreen && fullscreenShellStyles]}
      data-testid='game-details-panel'
      data-fullscreen={isFullscreen}
    >
      <div css={[containerStyles, isFullscreen && fullscreenColumnStyles]}>
        {bulk.summary !== null && (
          <BulkFailurePanel
            summary={bulk.summary}
            describeRow={describeRow}
            onDismiss={bulk.dismissSummary}
          />
        )}

        <GridToolbar
          toolbar={toolbar}
          isFullscreen={isFullscreen}
          onToggleFullscreen={() => setIsFullscreen((current) => !current)}
        />

        {canEdit && (
          <BulkToolbar
            selectedCount={selectedGames.length}
            venues={Array.from(venuesById.values())}
            gameTypes={gameTypes}
            disabled={bulk.isRunning || selectedGames.length === 0}
            preview={preview}
            onApply={applyBulk}
          />
        )}

        <DetailsGrid
          rows={rows}
          venuesById={venuesById}
          teamsById={teamsById}
          teamsLoading={teamsLoading}
          gameTypes={gameTypes}
          canEdit={canEdit}
          showTimeZoneInHeader={showTimeZoneInHeader}
          officials={officialOptions.officials}
          officialNamesById={officialOptions.officialNamesById}
          officialTypes={officialTypes}
          canAssignOfficials={officialOptions.canAssign}
          loading={loading}
          /*
           * Order is held while a run is in flight, and while a summary is up.
           *
           * A clean run no longer raises a summary — the strip of counts after
           * every successful edit was noise — so a row edited under an active sort
           * now settles into its new position as soon as the write lands, rather
           * than waiting for a dismissal that never comes. The freeze still covers
           * the cases that raise a panel: a failure to review, or a row the edit
           * pushed outside the active filter.
           */
          suppressReordering={bulk.isRunning || bulk.isSummaryOpen}
          fillHeight={isFullscreen}
          popupParent={isFullscreen ? shellEl : null}
          onSelectionChanged={(selected) =>
            setSelectedIds(selected.map((row) => row.id))
          }
          onCellAction={applyCell}
          onEditGame={canEdit ? setEditingRow : undefined}
          onReachBottom={onReachBottom}
          onGridApiReady={setGridApi}
          onDisplayedRowsChanged={setGridStatus}
        />

        {/*
          Keyed on the row id so the modal reseeds when a DIFFERENT game is
          opened: its form state is seeded on open, and reusing the instance
          would leave the previous game's values in place.

          The mutations invalidate with `refetchType: 'none'` — right for views
          the admin is not looking at, wrong for this one, which is on screen.
          `onGameChanged` refetches it explicitly so the row updates in place.

          `scheduleId` is the HOST's competition, not the row's — a row's
          `schedule` is the division, and scoping the modal to it returns the
          wrong team pool. See `resolveEditModalScheduleId`.
        */}
        {editingRow && (
          <GameModalContainer
            key={editingRow.id}
            isOpen
            onClose={() => setEditingRow(null)}
            mode='edit'
            game={editingRow.game}
            scheduleId={resolveEditModalScheduleId(scheduleId, editingRow.game)}
            organizationId={organizationId}
            organizationName={organizationName}
            sportId={sportId}
            onSaved={onGameChanged}
            onDeleted={onGameChanged}
            canDelete={canDeleteGames}
          />
        )}

        <GridStatusBar
          loadedCount={rows.length}
          displayedCount={gridStatus.displayed}
          selectedCount={selectedIds.length}
          isFiltered={gridStatus.isFiltered}
          hasMorePages={counts.hasUnloadedRawPages}
        />

        {/*
          Details pages by scrolling, but select-all only ever selects LOADED
          rows — so an admin can bulk edit a set that silently excludes games
          that were never fetched. The incomplete state and a way to finish
          loading have to be visible before that happens.
        */}
        <LoadMoreFooter
          counts={counts}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={loading}
          visibleCount={rows.length}
          showIncomplete
          isError={isError}
          errorMessage={errorMessage}
          onRetry={onRetry}
          isLoadingAll={isLoadingAll}
          onLoadAll={onLoadAll}
        />
      </div>
    </div>
  )
}

/** Transparent in page flow; the full-screen shell is the same node. */
const shellStyles = css`
  width: 100%;
  min-width: 0;
`

const containerStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`

/**
 * Full screen: the sheet takes the window, and everything that steers it comes
 * along — search, columns, bulk bar, counts, and the loader that says the set is
 * incomplete. A spreadsheet without its own toolbar is a screenshot.
 *
 * `position: fixed` rather than the browser's Fullscreen API on purpose. Both
 * cell pickers portal their panels into `document.body`, which is OUTSIDE any
 * element `requestFullscreen()` would promote — the calendar and the time list
 * would simply not render. This shell leaves them where they are.
 *
 * Sits one step above `zIndex.header` (the fixed nav and the page's own header),
 * and deliberately BELOW the antd panels: the picker opens at 1050 and
 * `TimePickerWithInterval` at 10002, so both keep painting over this without
 * either of them needing to know the shell exists.
 *
 * NO padding — see the note on the render. AG Grid positions a popup against
 * this element's padding box.
 */
const fullscreenShellStyles = css`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.overlay};
  background: ${colors.SECONDARY_400};
  overflow: hidden;
`

/** Carries the inset the shell cannot, and gives the grid a height to fill. */
const fullscreenColumnStyles = css`
  height: 100%;
  padding: 16px 24px 8px;
`

export default DetailsPanel
